import _ from "lodash";
import Api from "services/Api";
import claimDetails from "store/modules/fatal/dependency/claimDetails";
import calculateLoss from "store/modules/fatal/dependency/calculateLoss";
import people from "store/modules/fatal/dependency/people";
import proceduralDates from "store/modules/fatal/dependency/proceduralDates";
import multiplicand from "store/modules/fatal/dependency/multiplicand";
import headsOfLoss from "store/modules/fatal/dependency/headsOfLoss";
import dependencyOnServices from "store/modules/fatal/dependency/dependencyOnServices";
import step from "store/modules/fatal/dependency/step";
import cru from "store/modules/fatal/dependency/cru";
import interimPayments from "store/modules/fatal/dependency/interimPayments";
import psla from "store/modules/psla";
import moment from 'moment';
import store from 'store/index';
import deceased from 'store/modules/fatal/dependency/deceased';
import dependants from 'store/modules/fatal/dependency/dependants';
import dependencyOnIncome from 'store/modules/fatal/dependency/dependencyOnIncome'
import {isStepValid} from 'services/routingMethods';
import documents from 'store/modules/lost/documents';

const mutationTypes = {
    LOADING_CLAIM: 'LOADING_CLAIM',
    UPDATING_TABLE: 'UPDATING_TABLE',
    CHANGE_CLAIM_ID: 'CHANGE_CLAIM_ID',
    FINISHED_LOADING_CLAIM: 'FINISHED_LOADING_CLAIM',
};

// Initial State
const state = {
    claimId: null
};

// Getters
const getters = {};

// Actions
const actions = {
    /**
     * Dispatches save requests to the correct step.
     *
     * @param rootGetters
     * @param payload
     */
    save({rootGetters}, payload) {
        let individualStep = rootGetters['fatal/dependency/step/allSteps'][payload.step_number];

        return store.dispatch('fatal/dependency/' + individualStep.store + '/save', payload);
    },

    /**
     * Dispatches loading of a step requests to the correct step.
     *
     * @param dispatch
     * @param rootGetters
     * @param step
     */
    load({dispatch, rootGetters}, step) {
        let individualStep = rootGetters['fatal/dependency/step/allSteps'][parseInt(step.step_number, 10)];

        dispatch('checkForStepStore', individualStep)
            .then(() => store.dispatch('fatal/dependency/' + individualStep.store + '/load', step));
    },

    /**
     * Dispatches process requests to the correct step.
     *
     * @param commit
     * @param dispatch
     * @param step
     */
    process({dispatch}, step) {
        dispatch('checkForStepStore', step)
            .then(() => store.dispatch('fatal/dependency/' + step.store + '/process', step))
            .then(() => {
                dispatch('updateMultipliers');
                dispatch('headsOfLoss/calcAllInterest');
            });
    },

    /**
     * Dispatches validate requests to the correct step.
     */
    validate({dispatch}, step) {
        dispatch('checkForStepStore', step)
            .then(() => {
                store.dispatch('fatal/dependency/' + step.store + '/validate', step)
            }).then(() => {
                dispatch('checkToUpdateMultipliers');
                dispatch('headsOfLoss/calcAllInterest');
            });
    },

    /**
     * Dispatches reset requests to the correct step.
     *
     * @param dispatch
     * @param step
     */
    reset({dispatch, rootState}, step) {
        dispatch('checkForStepStore', step)
            .then(() => {
                // Only attempt to reset steps on this store.
                if (rootState.fatal.dependency[step.store]) {
                    store.dispatch('fatal/dependency/' + step.store + '/reset', step);
                }
            });
    },

    /**
     * Checks that a Step has its store defined in the routes, otherwise throws an error.
     *
     * @param individualStep
     */
    checkForStepStore(none, individualStep) {
        if (!individualStep.store) {
            throw {'error': 'Step #' + individualStep.step + ' (' + individualStep.name + ') does not have a store defined in the routes meta'};
        }
    },

    /**
     * If a step is valid dispatch updates
     *
     * @param state
     * @param dispatch
     * @return {Promise}
     */
    checkToUpdateMultipliers({state, dispatch}) {
        if (isStepValid(1) && isStepValid(2) && isStepValid(3) && isStepValid(4)) {
            return dispatch('updateMultipliers');
        }
        if (isStepValid(1) && isStepValid(2) && isStepValid(3) && isStepValid(4) && isStepValid(5)) {
            return Promise.all([
                dispatch('updateServices'),
                dispatch('updateServicesTable')
            ]);
        }
        return new Promise(resolve => resolve(true));
    },

    /**
     * Update the dependant
     *
     * @param dispatch
     * @param key
     */
    dependantUpdate({dispatch}, key) {
        switch (key) {
            case 'dateOfBirth':
                dispatch('proceduralDates/determineAgeAtSettlement');
                break;
            case 'dateOfDeath':
                dispatch('proceduralDates/determineAgeOfDeathToSettlement');
                dispatch('headsOfLoss/determineBereavementAward');
                break;
        }
    },

    /**
     * Update the multiplicand calculations
     *
     * @param dispatch
     * @param state
     */
    updateTheMultiplicand: _.throttle(({state, dispatch}) => {
        const deceasedIncome = state.deceased.theDeceased.income.periods.map(function (period) {
            let startOfPeriod = moment(period.startOfPeriod, 'DD/MM/YYYY');
            let endOfPeriod = moment(period.endOfPeriod, 'DD/MM/YYYY');
            let days = endOfPeriod.diff(startOfPeriod, 'days');
            return {
                days: days,
                multipliedNetIncome: period.totalNetIncome * days,
            }
        }).reduce(function (total, period) {
            total[0].days += period.days;
            total[0].multipliedNetIncome += period.multipliedNetIncome;
            return total;
        }, [{
            days: 0,
            multipliedNetIncome: 0,
        }]).reduce(function (total, result) {
            return result.multipliedNetIncome / result.days;
        }, 0);

        return Api.calculators.monetary.multiplicand({net_income: deceasedIncome}, state.dependants.theDependants, state.multiplicand.percentage)
            .then(total => dispatch('multiplicand/updateTheValue', total));
    }, 500),

    /**
     * Update the multiplier calculations
     *
     * @param dispatch
     * @param state
     * @param getters
     */
    updateMultipliers: _.debounce(({dispatch, state, getters}) => {
        const multiTotal = getters['multiplicand/determineTotal'];

        if (!state.deceased.theDeceased.lifeExpectancy.dateOfDeath) { return; }

        return Api.calculators.monetary.multiplier(
            state.deceased.theDeceased,
            store.getters['fatal/dependency/dependants/dependants'],
            state.claimDetails.claimantName,
            state.claimDetails.assumedSettlement,
            'financial',
            getters['claimDetails/value']('discountRate'),
            getters['dependencyOnIncome/value']('discountTable'),
            multiTotal
        )
            .then(data => dispatch('dependencyOnIncome/updateFromServer', data)
                .then(() => dispatch('updateMultiplierTable')));

    }, 500),

    /**
     * Update the multiplier table
     *
     * @param dispatch
     * @param state
     * @param getters
     */
    updateMultiplierTable({dispatch, state, getters}) {
        const multiTotal = getters['multiplicand/determineTotal'];
        const discountTable = getters['dependencyOnIncome/discountTable'];
        let dispatchUrl = 'dependencyOnIncome/updateTable1and2FromClaimLoad';

        if (state.dependants.theDependants.length < 1) {
            return;
        }

        if (discountTable === 28) {
            dispatchUrl = 'dependencyOnIncome/updateTable28FromClaimLoad';
        }

        return Api.calculators.monetary.multiplierTable(
            state.deceased.theDeceased,
            state.dependants.theDependants,
            state.claimDetails.claimantName,
            state.claimDetails.assumedSettlement,
            getters['claimDetails/value']('discountRate'),
            getters['dependencyOnIncome/value']('discountTable'),
            multiTotal,
        ).then(data => dispatch(dispatchUrl, data));
    },

    /**
     * Update the dependency on services
     *
     * @param dispatch
     * @param state
     * @param getters
     */
    updateServices: _.debounce(({dispatch, state, getters}) => {
        dispatch('dependencyOnServices/updateServiceStatus');
        const services = state.dependencyOnServices.list;
        const preTrial = getters['dependants/preTrialDependency'];
        if (!services.length) return;
        return Api.calculators.monetary.servicesMultiplier(
            state.deceased.theDeceased,
            state.claimDetails.claimantName,
            state.claimDetails.assumedSettlement,
            services,
            preTrial,
            getters['claimDetails/discountRate'],
        ).then((data) => {
            const proms = data.map((data, index) => dispatch('dependencyOnServices/updateFromServer', {data, index}));
            dispatch('dependencyOnServices/updateServiceStatus');
            return Promise.all(proms);
        });
    }, 500),

    /**
     * Update the dependency on services table
     *
     * @param dispatch
     * @param state
     * @param getters
     */
    updateServicesTable: _.debounce(({dispatch, state, getters}) => {
        const services = state.dependencyOnServices.list;
        const preTrial = getters['dependants/preTrialDependency'];
        return Api.calculators.monetary.servicesTable(
            state.deceased.theDeceased,
            state.claimDetails.claimantName,
            state.claimDetails.assumedSettlement,
            services,
            preTrial,
            getters['claimDetails/discountRate'],
            getters['dependencyOnIncome/discountTable'],
        ).then((data) => {
            const proms = data.map((data, index) => dispatch('dependencyOnServices/updateTableFromServer', {data, index}));
            return Promise.all(proms);
        });
    }, 1000),
};

const mutations = {
    [mutationTypes.CHANGE_CLAIM_ID](state, payload) {
        state.claimId = payload;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
    modules: {
        calculateLoss,
        claimDetails,
        people,
        proceduralDates,
        multiplicand,
        dependencyOnServices,
        headsOfLoss,
        cru,
        interimPayments,
        psla,
        step,
        deceased,
        dependants,
        dependencyOnIncome,
        documents
    },
};



// WEBPACK FOOTER //
// ./src/store/modules/fatal/dependency/index.js